import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Crown,
    Lightning,
    Sparkle,
    Receipt,
    Clock,
    CheckCircle,
    ArrowRight,
    Calendar,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch {
        return "—";
    }
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function CandidateSubscriptions() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/me/purchases")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    const pro = data?.pro || {};
    const boosts = data?.application_boosts || [];
    const rewrites = data?.cv_rewrites || [];
    const txns = data?.transactions || [];
    const boostCredits = data?.boost_credits || 0;
    const bundle = data?.boost_bundle;
    const [bundleBuying, setBundleBuying] = useState(false);

    const buyBundle = async () => {
        setBundleBuying(true);
        try {
            const rzOk = await new Promise((resolve) => {
                if (window.Razorpay) return resolve(true);
                const s = document.createElement("script");
                s.src = "https://checkout.razorpay.com/v1/checkout.js";
                s.onload = () => resolve(true);
                s.onerror = () => resolve(false);
                document.body.appendChild(s);
            });
            if (!rzOk) throw new Error("Couldn't load payment gateway");
            const { data: order } = await api.post("/me/boost-bundle/checkout");
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy",
                description: `Boost 3-pack · ${order.credits} credits`,
                theme: { color: "#171717" },
                handler: async (rp) => {
                    try {
                        const { data: verify } = await api.post("/me/boost-bundle/verify", {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        // Refetch purchases to show new credit balance
                        api.get("/me/purchases").then((r) => setData(r.data));
                        toast?.success?.(`+${verify.credits_added || bundle.credits} boost credits added`);
                    } catch (err) {
                        toast?.error?.("Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBundleBuying(false) },
            });
            rz.open();
        } catch (err) {
            toast?.error?.("Couldn't start checkout");
        } finally {
            setBundleBuying(false);
        }
    };

    const activeBoosts = boosts.filter((b) => b.active);
    const expiredBoosts = boosts.filter((b) => !b.active);

    // Pro pass progress ring math
    const proPct = pro.active
        ? Math.max(4, Math.min(100, ((30 - (pro.days_remaining || 0)) / 30) * 100))
        : 0;

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="candidate-subscriptions-page">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                            My purchases
                        </p>
                        <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                            Subscriptions & receipts
                        </h1>
                        <p className="text-sm text-[#525252] mt-1">
                            Everything you&apos;ve purchased on NaukriDaddy.
                        </p>
                    </div>
                    {data?.total_spent_inr > 0 && (
                        <div className="bg-white border border-[#E5E5E5] px-5 py-3">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                                Total spent
                            </p>
                            <p className="font-heading text-2xl font-black tracking-tight text-ink">
                                {fmtINR(data.total_spent_inr)}
                            </p>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className="text-sm text-[#A3A3A3]" data-testid="subs-loading">Loading your purchases…</div>
                )}

                {!loading && (
                    <>
                        {/* Pro pass status */}
                        <section data-testid="subs-pro-section" className="mb-8">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                                NaukriDaddy Pro
                            </p>
                            {pro.active ? (
                                <div className="bg-ink text-white p-5 sm:p-7 relative overflow-hidden">
                                    <div className="absolute -right-16 -top-16 w-48 h-48 bg-splash/20 rounded-full blur-3xl pointer-events-none" />
                                    <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-6">
                                        <div className="sm:col-span-2">
                                            <div className="inline-flex items-center gap-2 bg-splash text-ink px-3 py-1 font-heading font-black text-xs uppercase tracking-wider mb-3">
                                                <Crown size={12} weight="fill" /> Pro · Active
                                            </div>
                                            <p className="font-heading text-2xl sm:text-3xl font-black tracking-tight mb-1">
                                                {pro.days_remaining} {pro.days_remaining === 1 ? "day" : "days"} remaining
                                            </p>
                                            <p className="text-sm text-white/60">
                                                Expires on <span className="text-white font-bold">{fmtDate(pro.pro_until)}</span> · One-time payment, no auto-renew.
                                            </p>
                                            <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                                <div
                                                    data-testid="subs-pro-progress"
                                                    className="h-full bg-splash transition-all"
                                                    style={{ width: `${proPct}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div className="border-t sm:border-t-0 sm:border-l border-white/10 sm:pl-6 pt-4 sm:pt-0">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-white/50 mb-1">Usage</p>
                                            <p className="text-sm text-white/80">
                                                CV rewrites used: <span className="font-heading text-lg font-black text-white">{pro.cv_rewrites_used || 0}</span>
                                                <span className="text-white/50 text-xs ml-1">/ Unlimited</span>
                                            </p>
                                            <p className="text-sm text-white/80 mt-2">
                                                Priority shortlist visibility: <span className="text-emerald-400 font-bold">ON</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white border border-[#E5E5E5] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <p className="font-heading text-lg font-bold tracking-tight text-ink mb-1">
                                            You&apos;re on the free plan
                                        </p>
                                        <p className="text-sm text-[#525252]">
                                            {pro.cv_rewrites_used || 0} of {pro.cv_rewrites_limit || 1} free CV rewrites used.
                                            Upgrade for unlimited rewrites + top-shortlist priority.
                                        </p>
                                    </div>
                                    <Link
                                        to="/pro"
                                        data-testid="subs-pro-upgrade"
                                        className="btn-gradient px-5 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 whitespace-nowrap"
                                    >
                                        <Crown size={14} weight="fill" /> Go Pro — ₹499
                                    </Link>
                                </div>
                            )}
                        </section>

                        {/* Application boosts */}
                        <section data-testid="subs-boosts-section" className="mb-8">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                    Application boosts
                                </p>
                                <p className="text-[10px] text-[#A3A3A3]">
                                    {activeBoosts.length} active · {expiredBoosts.length} expired
                                </p>
                            </div>
                            {activeBoosts.length === 0 && expiredBoosts.length === 0 ? (
                                <div className="bg-white border border-[#E5E5E5] p-5 text-center">
                                    <Lightning size={24} weight="fill" className="text-splash mx-auto mb-2" />
                                    <p className="text-sm text-[#525252]">
                                        No boosts yet. Boost an application for ₹49 to top the recruiter shortlist for 7 days.
                                    </p>
                                    <Link
                                        to="/candidate"
                                        className="text-cobalt font-bold text-xs uppercase tracking-wider mt-3 inline-flex items-center gap-1"
                                    >
                                        View my applications <ArrowRight size={11} weight="bold" />
                                    </Link>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {[...activeBoosts, ...expiredBoosts].map((b) => (
                                        <div
                                            key={b.id}
                                            data-testid={`subs-boost-${b.id}`}
                                            className={`bg-white border p-4 flex items-center justify-between gap-3 ${
                                                b.active ? "border-splash" : "border-[#E5E5E5]"
                                            }`}
                                        >
                                            <div className="flex items-start gap-3 min-w-0">
                                                <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${
                                                    b.active ? "bg-splash text-ink" : "bg-[#F5F5F5] text-[#A3A3A3]"
                                                }`}>
                                                    <Lightning size={16} weight="fill" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-heading text-sm font-bold tracking-tight truncate">
                                                        {b.job_title || "Application boost"}
                                                        {b.client_name ? <span className="text-[#A3A3A3] font-normal"> · {b.client_name}</span> : null}
                                                    </p>
                                                    <p className="text-[11px] text-[#525252]">
                                                        {b.active ? (
                                                            <><span className="text-emerald-600 font-bold">Active</span> · {b.days_remaining} {b.days_remaining === 1 ? "day" : "days"} left · until {fmtDate(b.active_until)}</>
                                                        ) : (
                                                            <>Expired on {fmtDate(b.active_until)}</>
                                                        )}
                                                        {" · "}{fmtINR(b.amount_inr)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Boost bundle upsell — shown when user has bought at least 1 boost. */}
                        {bundle && (boosts.length > 0 || boostCredits > 0) && (
                            <section data-testid="subs-bundle-upsell" className="mb-8">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                                    Boost credits
                                </p>
                                <div className="bg-ink text-white p-5 sm:p-7 relative overflow-hidden">
                                    <div className="absolute -right-16 -top-16 w-48 h-48 bg-splash/20 rounded-full blur-3xl pointer-events-none" />
                                    <div className="relative flex items-start justify-between gap-6 flex-wrap">
                                        <div className="min-w-0">
                                            <div className="inline-flex items-center gap-2 bg-splash text-ink px-3 py-1 font-heading font-black text-xs uppercase tracking-wider mb-3">
                                                <Lightning size={12} weight="fill" />
                                                {boostCredits > 0
                                                    ? `${boostCredits} credit${boostCredits === 1 ? "" : "s"} available`
                                                    : `Save ${bundle.savings_pct}% · Boost 3-pack`}
                                            </div>
                                            <p className="font-heading text-xl sm:text-2xl font-black tracking-tight mb-1">
                                                {boostCredits > 0
                                                    ? "Your next application boost is free"
                                                    : `Buy 3 boosts, get ${bundle.savings_pct}% off`}
                                            </p>
                                            <p className="text-sm text-white/60 leading-relaxed">
                                                {boostCredits > 0
                                                    ? "Just tap Boost on any application — 1 credit auto-applies."
                                                    : `${fmtINR(bundle.price_inr)} for 3 boosts (₹${bundle.per_boost_price_inr} each) instead of ${fmtINR(bundle.list_price_inr)}. Credits never expire.`}
                                            </p>
                                        </div>
                                        {boostCredits === 0 && (
                                            <button
                                                onClick={buyBundle}
                                                disabled={bundleBuying}
                                                data-testid="subs-bundle-buy"
                                                className="btn-gradient px-5 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-60"
                                            >
                                                <Lightning size={14} weight="fill" />
                                                {bundleBuying ? "Opening…" : `Buy 3-pack ${fmtINR(bundle.price_inr)}`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* CV rewrites purchased */}
                        {rewrites.length > 0 && (
                            <section data-testid="subs-rewrites-section" className="mb-8">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                                    AI CV rewrites purchased
                                </p>
                                <div className="space-y-2">
                                    {rewrites.map((r) => (
                                        <div key={r.id} className="bg-white border border-[#E5E5E5] p-4 flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 bg-cobalt/10 text-cobalt flex items-center justify-center shrink-0">
                                                    <Sparkle size={16} weight="fill" />
                                                </div>
                                                <div>
                                                    <p className="font-heading text-sm font-bold tracking-tight">
                                                        AI CV Rewrite{r.with_human_review ? " + Human Review" : ""}
                                                    </p>
                                                    <p className="text-[11px] text-[#525252]">
                                                        {fmtDate(r.paid_at)} · {fmtINR(r.amount_inr)}
                                                    </p>
                                                </div>
                                            </div>
                                            <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Transaction history */}
                        <section data-testid="subs-txns-section" className="mb-4">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                    All transactions
                                </p>
                                <p className="text-[10px] text-[#A3A3A3]">
                                    {txns.length} {txns.length === 1 ? "transaction" : "transactions"}
                                </p>
                            </div>
                            {txns.length === 0 ? (
                                <div className="bg-white border border-[#E5E5E5] p-5 text-center">
                                    <Receipt size={24} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                                    <p className="text-sm text-[#525252]">
                                        No transactions yet. All future purchases will show up here as receipts.
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                                    {txns.map((t, i) => (
                                        <div
                                            key={i}
                                            data-testid={`subs-txn-${i}`}
                                            className="px-4 sm:px-5 py-3 flex items-center justify-between gap-3"
                                        >
                                            <div className="min-w-0">
                                                <p className="font-heading text-sm font-bold tracking-tight truncate">
                                                    {t.product}
                                                </p>
                                                <p className="text-[11px] text-[#A3A3A3] flex items-center gap-1.5">
                                                    <Clock size={10} weight="bold" /> {fmtDate(t.paid_at)}
                                                    {t.order_id ? <span className="hidden sm:inline">· Order {t.order_id.slice(-10)}</span> : null}
                                                </p>
                                            </div>
                                            <p className="font-heading text-sm font-black tracking-tight text-ink shrink-0">
                                                {fmtINR(t.amount_inr)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        <p className="text-[10px] text-[#A3A3A3] mt-6 flex items-center gap-1.5">
                            <Calendar size={11} weight="bold" />
                            All charges are processed by Razorpay · Refund requests: hello@naukridaddy.com
                        </p>
                    </>
                )}
            </div>
    );
}
